/**
 * ShadowUniforms.ts — uniform buffer holding the sun light's view-projection
 * matrix (reverse-Z) plus PCF parameters. Filled by ShadowPass each frame.
 *
 * WGSL layout:
 *   lightViewProj : mat4x4<f32>  (64)
 *   invShadowRes  : f32
 *   depthBias     : f32           (in shadow-NDC units, reverse-Z so positive = bias TOWARD light)
 *   pcfRadius     : f32           (texels)
 *   _pad          : f32
 *   Total: 80 bytes
 */

import { mat4, vec3 } from "wgpu-matrix";
import { createUniformBuffer } from "../gpu/Buffers";

const STRUCT_SIZE = 80;

export class ShadowUniforms {
  readonly buffer: GPUBuffer;
  readonly resolution: number;
  /** Half-extent of orthographic frustum (metres). Covers the scene basin. */
  readonly orthoHalfExtent: number;
  /** Distance from look-at point along the inverse sun direction. */
  readonly lightDistance: number;
  /** Reverse-Z near/far for the light's frustum. */
  readonly nearPlane: number;
  readonly farPlane: number;

  private cpu = new ArrayBuffer(STRUCT_SIZE);
  private f32 = new Float32Array(this.cpu);

  /** Last computed light view-projection (read by ShadowPass). */
  lightViewProj = mat4.identity();

  pcfRadiusTexels = 1.5;
  depthBias = 3e-5;

  /** XZ position the shadow ortho frustum is centred on (metres, world).
   *  Set this to the player boat each frame so the shadow map follows the
   *  action — at the default 250 m half-extent this gives ~0.24 m / texel
   *  which is enough to actually resolve a 9 m hull's shadow on water. */
  centerXZ: [number, number] = [0, 0];

  constructor(
    device: GPUDevice,
    opts: {
      resolution?: number;
      orthoHalfExtent?: number;
      lightDistance?: number;
      near?: number;
      far?: number;
    } = {},
  ) {
    this.resolution = opts.resolution ?? 2048;
    // Tight ortho centred on the player boat. Texel = 2*extent/res =
    // 500/2048 ≈ 0.24 m, so a hull beam of 2–3 m covers ~10 texels and the
    // PCF kernel produces a soft but visible shadow on water.
    this.orthoHalfExtent = opts.orthoHalfExtent ?? 250;
    this.lightDistance = opts.lightDistance ?? 1500;
    this.nearPlane = opts.near ?? 1;
    this.farPlane = opts.far ?? 2000;
    this.buffer = createUniformBuffer(device, STRUCT_SIZE, "ShadowUniforms");
  }

  /**
   * Recompute lightViewProj from sun direction and upload.
   *
   * The sun direction points TOWARD the sun (lighting convention). The light
   * camera looks at the scene origin from `lightDistance * sunDir` along that
   * inverse direction, with an orthographic frustum that covers the basin.
   *
   * Reverse-Z convention: same as main camera, near maps to z=1, far to z=0.
   */
  update(device: GPUDevice, sunDir: [number, number, number]): void {
    // Centre the orthographic frustum on the player boat (XZ) so the shadow
    // map follows the action. Y stays at the water plane (0) — that's where
    // the interesting shadow receivers are.
    //
    // Texel snapping: as the boat moves, the light frustum slides smoothly
    // and the shadow-map's texel grid drifts sub-pixel under thin objects
    // (mast, shrouds, lifelines), making them flicker badly. Snapping the
    // centre to multiples of one shadow texel locks the grid to world space
    // so thin features stay stable. We compute the snap in the basis of
    // the light's view (right=worldX, up=worldZ for an overhead sun is
    // close enough; the orthographic ground plane is XZ).
    const texelSize = (2 * this.orthoHalfExtent) / this.resolution;
    const cx = Math.round(this.centerXZ[0] / texelSize) * texelSize;
    const cz = Math.round(this.centerXZ[1] / texelSize) * texelSize;
    // Eye position: above the scene centre along the sun direction.
    const eye = vec3.create(
      cx + sunDir[0] * this.lightDistance,
      sunDir[1] * this.lightDistance,
      cz + sunDir[2] * this.lightDistance,
    );
    const center = vec3.create(cx, 0, cz);
    // Pick an "up" vector that is NOT parallel to the sun direction.
    let up: Float32Array;
    if (Math.abs(sunDir[1]) > 0.95) {
      up = vec3.create(0, 0, 1);
    } else {
      up = vec3.create(0, 1, 0);
    }
    const view = mat4.lookAt(eye, center, up);

    // Reverse-Z orthographic projection. Standard ortho maps near→-1, far→+1
    // in NDC z; we want near→+1, far→0. Build manually.
    const l = -this.orthoHalfExtent;
    const r = this.orthoHalfExtent;
    const b = -this.orthoHalfExtent;
    const t = this.orthoHalfExtent;
    const n = this.nearPlane;
    const f = this.farPlane;
    const proj = mat4.identity();
    // Column-major (wgpu-matrix Float32Array layout).
    proj[0] = 2 / (r - l);
    proj[5] = 2 / (t - b);
    // Reverse-Z orthographic projection. We look down -z (right-handed view).
    // Goal: view_z = -n  → ndc_z = 1   (near plane)
    //       view_z = -f  → ndc_z = 0   (far plane)
    // Solve: ndc_z = (view_z + f) / (f - n)  →  matrix row: [0,0, 1/(f-n), f/(f-n)]
    proj[10] = 1 / (f - n);
    proj[14] = f / (f - n);
    proj[12] = -(r + l) / (r - l);
    proj[13] = -(t + b) / (t - b);
    proj[15] = 1;

    mat4.multiply(proj, view, this.lightViewProj);

    // Write to buffer.
    for (let i = 0; i < 16; i++) this.f32[i] = this.lightViewProj[i];
    this.f32[16] = 1 / this.resolution;
    this.f32[17] = this.depthBias;
    this.f32[18] = this.pcfRadiusTexels;
    this.f32[19] = 0;
    device.queue.writeBuffer(this.buffer, 0, this.cpu);
  }
}
